import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
  Grid,
  alpha,
  useTheme
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';

const LineItemsSection = ({ data, formatCurrency }) => {
  const theme = useTheme();
  const { lineItems, productSelections } = data;
  const [expandedItem, setExpandedItem] = useState(null);

  // Use line items if available, otherwise use product selections
  const items = lineItems && lineItems.length > 0 ? lineItems : productSelections || [];

  if (!items || items.length === 0) {
    return null;
  }

  const handleExpandClick = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'cancelled': return 'error';
      case 'backordered': return 'warning';
      case 'substituted': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.warning.main, 0.1)
          }}>
            <CartIcon sx={{ fontSize: 28, color: 'warning.main' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Line Items
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {items.length} item{items.length !== 1 ? 's' : ''} in this order
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                <TableCell sx={{ fontWeight: 500, width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>Product</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>UoM</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Quantity</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Unit Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, width: 40 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const isExpanded = expandedItem === item.id;
                const hasDetails = item.lot_number || item.batch_number || item.manufactured_date || 
                                   item.expiry_date || item.location_name || item.inventory_item_id;

                return (
                  <React.Fragment key={item.id}>
                    <TableRow 
                      hover 
                      sx={{ 
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        borderLeft: isExpanded ? `3px solid ${theme.palette.primary.main}` : 'none'
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {item.product_name || item.description}
                          </Typography>
                          {item.variant_name && (
                            <Typography variant="caption" color="text.secondary">
                              Variant: {item.variant_name}
                            </Typography>
                          )}
                          {item.product_sku && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              SKU: {item.product_sku}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={500}>
                          {item.units_per_case ? `${item.units_per_case}U/C` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={500}>
                          {(() => {
                            const qty = Math.round(parseFloat(item.quantity || 0));
                            const isCase = (item.unit_type || 'Case') === 'Case';
                            return isCase ? `${qty} ${qty === 1 ? 'Case' : 'Cases'}` : `${qty}`;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(item.unit_price)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500} color="primary.main">
                          {formatCurrency(item.line_total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={(() => {
                            const status = item.line_status || 'active';
                            // Capitalize first letter
                            return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                          })()} 
                          color={getStatusColor(item.line_status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {hasDetails && (
                          <IconButton
                            size="small"
                            onClick={() => handleExpandClick(item.id)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasDetails && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: alpha(theme.palette.primary.main, 0.03),
                              borderRadius: 2,
                              my: 1
                            }}>
                              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 2, color: 'primary.main' }}>
                                Additional Details
                              </Typography>
                              <Grid container spacing={2}>
                                {item.lot_number && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Lot Number
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {item.lot_number}
                                    </Typography>
                                  </Grid>
                                )}
                                {item.batch_number && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Batch Number
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {item.batch_number}
                                    </Typography>
                                  </Grid>
                                )}
                                {item.manufactured_date && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Mfg Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {formatDate(item.manufactured_date)}
                                    </Typography>
                                  </Grid>
                                )}
                                {item.expiry_date && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Exp Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {formatDate(item.expiry_date)}
                                    </Typography>
                                  </Grid>
                                )}
                                {item.location_name && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Location
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {item.location_name}
                                    </Typography>
                                  </Grid>
                                )}
                                {item.shelf_life_remaining !== null && item.shelf_life_remaining !== undefined && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Shelf Life Remaining
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight={500}
                                      color={item.shelf_life_remaining < 30 ? 'error.main' : item.shelf_life_remaining < 90 ? 'warning.main' : 'success.main'}
                                    >
                                      {item.shelf_life_remaining} days
                                    </Typography>
                                  </Grid>
                                )}
                                {item.backorder_quantity > 0 && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Backorder Qty
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} color="warning.main">
                                      {Math.round(parseFloat(item.backorder_quantity))}
                                    </Typography>
                                  </Grid>
                                )}
                                {item.discount_percent > 0 && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase' }}>
                                      Discount
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} color="success.main">
                                      {item.discount_percent}% ({formatCurrency(item.discount_amount)})
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                              {item.line_notes && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.2)}` }}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.5 }}>
                                    Notes
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {item.line_notes}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Row */}
        <Box sx={{ 
          mt: 2, 
          pt: 2, 
          borderTop: `2px solid ${alpha(theme.palette.grey[400], 0.3)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="subtitle1" fontWeight={500}>
            Total Items
          </Typography>
          <Typography variant="h6" fontWeight={500} color="primary.main">
            {items.length}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LineItemsSection;